import { scrubSecrets } from "./scrubSecrets";

/**
 * In-memory ring buffer of renderer console output + uncaught errors, so a
 * support report can ship "the browser logs" (the desktop today only forwards
 * renderer *errors* to eriol.log). Bounded and fail-safe: a console hook must
 * never throw or recurse. Call {@link initConsoleBuffer} once at startup.
 */

interface ConsoleEntry {
  ts: string;
  level: string;
  text: string;
}

const MAX_ENTRIES = 500;
const MAX_TEXT_PER_ENTRY = 2000;

const buffer: ConsoleEntry[] = [];
let initialized = false;

function stringifyArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ""}`;
  }
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function push(level: string, args: unknown[]): void {
  try {
    const text = args.map(stringifyArg).join(" ").slice(0, MAX_TEXT_PER_ENTRY);
    buffer.push({ ts: new Date().toISOString(), level, text });
    if (buffer.length > MAX_ENTRIES) buffer.shift();
  } catch {
    /* a console hook must never throw */
  }
}

export function initConsoleBuffer(): void {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  const levels = ["log", "info", "warn", "error", "debug"] as const;
  const target = console as unknown as Record<
    string,
    ((...a: unknown[]) => void) | undefined
  >;
  for (const level of levels) {
    const original = target[level]?.bind(console);
    target[level] = (...args: unknown[]) => {
      push(level, args);
      original?.(...args);
    };
  }

  window.addEventListener("error", (e) => {
    push("error", [
      `[window.onerror] ${e.message}`,
      e.error instanceof Error
        ? (e.error.stack ?? "")
        : `${e.filename}:${e.lineno}:${e.colno}`,
    ]);
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    push("error", [
      "[unhandledrejection]",
      reason instanceof Error
        ? `${reason.name}: ${reason.message}${
            reason.stack ? `\n${reason.stack}` : ""
          }`
        : String(reason),
    ]);
  });
}

/** Serialize the buffer (scrubbed) for attaching to a support report. */
export function dumpConsoleBuffer(): string {
  const lines = buffer.map(
    (e) => `[${e.ts}] ${e.level.toUpperCase()}: ${e.text}`,
  );
  return scrubSecrets(lines.join("\n"));
}

/** Number of buffered entries (for the "what we'll send" preview). */
export function consoleBufferSize(): number {
  return buffer.length;
}
